/**
 * 会话超时配置类型定义
 *
 * @description 会话超时相关的系统配置
 */

/**
 * 超时跳转方式
 */
export enum SessionExpiredMode {
  /** 弹窗模式 - 显示弹窗提示用户重新登录 */
  MODAL = 'modal',
  /** 重定向模式 - 直接跳转到登录页 */
  REDIRECT = 'redirect',
}

/**
 * 会话超时配置
 */
export interface SessionTimeoutConfig {
  /** 是否启用会话超时检测 */
  enabled: boolean;
  /** 超时时间（分钟） */
  timeoutMinutes: number;
  /** 警告提前时间（分钟） */
  warningMinutes: number;
  /** 超时跳转方式 */
  expiredMode: SessionExpiredMode;
}

/**
 * Token 刷新策略配置
 *
 * @description 方案B：只在"快过期"时才刷新Token
 *
 * 工作原理：
 * - Token有效期: 30分钟
 * - 刷新窗口: 最后5分钟
 * - 0-25分钟: 用户活动 → 只重置会话计时器，不刷新Token
 * - 25-30分钟: 用户活动 → 重置会话计时器 + 刷新Token
 *
 * 优势：每个活跃用户每25分钟最多1次刷新请求，大幅降低服务器压力
 */
export interface TokenRefreshConfig {
  /** Token 有效期（分钟），从登录响应获取 */
  tokenExpiresIn: number;
  /** 刷新窗口（分钟），剩余时间小于此值时才刷新 */
  refreshWindowMinutes: number;
  /** Token 创建时间戳 */
  tokenCreatedAt: number;
}

/**
 * 后端返回的配置格式
 */
export interface SessionConfigResponse {
  /** 是否启用会话超时检测 */
  session_timeout_enabled?: boolean;
  /** 超时时间（分钟） */
  session_timeout_minutes?: number;
  /** 警告提前时间（分钟） */
  session_warning_minutes?: number;
  /** 超时跳转方式 */
  session_expired_mode?: string;
  /** Token 有效期（分钟） */
  token_expires_in?: number;
  /** Token 刷新窗口（分钟） */
  token_refresh_window_minutes?: number;
}

/**
 * 默认配置
 *
 * @description 当后端配置加载失败时使用的默认配置
 * - 超时时间：30分钟（与后端 Redis 会话超时一致）
 * - 警告时间：5分钟（提前警告）
 * - 超时模式：弹窗模式（用户体验更好）
 *
 * @note 前端会话超时必须与后端一致（30分钟），否则会出现：
 *       - 后端已判定超时返回 401，但前端还没有显示警告
 *       - 用户体验不一致
 *       Token有效期（后端配置）：Web 30分钟 / App 7天
 *
 * @note 开发环境下可以临时修改为更短的时间以便测试
 */
export const DEFAULT_SESSION_CONFIG: SessionTimeoutConfig = {
  enabled: true,
  timeoutMinutes: 30, // 与后端 Redis 会话超时一致
  warningMinutes: 5,
  expiredMode: SessionExpiredMode.MODAL,
};

/**
 * 默认 Token 刷新配置
 *
 * @description 方案B：只在"快过期"时才刷新Token
 *
 * | 策略       | 每用户请求频率 | 1000活跃用户/小时 |
 * |------------|--------------|------------------|
 * | 每次活动刷新 | 几百次/小时   | 几十万次 ❌        |
 * | 每分钟刷新   | 60次/小时    | 6万次 ❌           |
 * | 快过期时刷新 | 最多2-3次/小时| 2000-3000次 ✅    |
 */
export const DEFAULT_TOKEN_REFRESH_CONFIG: Omit<
  TokenRefreshConfig,
  'tokenCreatedAt'
> = {
  tokenExpiresIn: 30, // Token 有效期 30 分钟
  refreshWindowMinutes: 5, // 最后 5 分钟内才刷新
};
